import DOMPurify from "dompurify"
import { htmlRegex, urlRegex, validEmail } from "../../../App/Regex"
import { useDispatch, useSelector } from "react-redux"
import { ReactTinyLink } from "react-tiny-link"
import { useState } from "react"
import { SmallCloseIcon } from "./SvgIcons"
import { setModalData } from "../../Shared/SharedSlices/ModalSlice"
import { setRoomPostId, setRoomCommentId, setRoomEventCommentId } from "../../Room/RoomPost/RoomPostSlice"
import { CallerComponent } from "../../../App/enums"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import useWindowSize from "../Hooks/useWindowSize"
import linkifyHtml from "linkify-html"

const normalizeFeedHtml = (html: string) => {
    const wrapper = document.createElement("div")
    wrapper.innerHTML = html

    wrapper.querySelectorAll<HTMLElement>("[style]").forEach(node => {
        node.removeAttribute("style")
    })

    const isEmptyNode = (node: Element) => {
        const withoutBreaks = node.innerHTML.replace(/<br\s*\/?>/gi, "").replace(/&nbsp;/gi, "").trim()
        return withoutBreaks.length === 0
    }

    wrapper.querySelectorAll("p, div").forEach(node => {
        if (isEmptyNode(node)) {
            node.remove()
        }
    })

    return wrapper.innerHTML.replace(/(<br\s*\/?>\s*){3,}/gi, "<br><br>")
}

function MessageBody(props: {
    text: string
    showPreview: boolean
    caller?: string
    id?: number
    showCollapsed: boolean
    showRemovePreviewIcon: boolean
    isCollapsed?: boolean
    date?: string
    toggleCollapse?: () => void
    onNavigationToExpandedPost?: () => void
    hashtags: Array<string | { text: string }>
}) {
    const dispatch = useDispatch()
    const translation = useSelector(selectTranslations)
    const [hasError, setHasError] = useState(false)
    const [hasImage, setHasImage] = useState(false)
    const screenWidth = useWindowSize()
    const breakpoint = 1023
    const maxContentLength = props.caller === CallerComponent.roomDescription ? 81 : screenWidth > breakpoint ? 500 : 300

    const cleanHtml = DOMPurify.sanitize(props.text, {
        ALLOWED_TAGS: ["b", "em","strong", "p", "span", "a", "div", "img", "header", "footer","ul","ol","li","br"],
        ALLOWED_ATTR: ["class", "href", "target", "data-type", "className", "id", "rel", "src", "style", "width", "type", "data-reactroot"],
    })

    const normalizedHtml = props.caller === CallerComponent.feed ? normalizeFeedHtml(cleanHtml) : cleanHtml

    const textWithoutHtml = normalizedHtml.replace(htmlRegex, "")

    const handleClick = (e : React.MouseEvent<HTMLDivElement,MouseEvent>) => {
        if(!(e.target instanceof HTMLAnchorElement)){
            if (props.onNavigationToExpandedPost){
                props.onNavigationToExpandedPost!()
            }
        }
    }

    const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!(e.target instanceof HTMLAnchorElement)) {
            e.preventDefault()
            e.stopPropagation()
            props.onNavigationToExpandedPost?.()
        }
    }

    const messageBodyClass = () => {
        if (props.showCollapsed) {
            if (!props.isCollapsed && textWithoutHtml.length > maxContentLength) return "truncated"
        } else if (hasLinkPreview() && props.caller === CallerComponent.feed) {
            return "message-body-link-feed"
        }
        return "message-body"
    }

    const hasLinkPreview = () => {
        const textWithoutHtml = normalizedHtml.replaceAll(htmlRegex, "")

        if (textWithoutHtml.match(urlRegex) && !textWithoutHtml.match(validEmail)) {
            if (textWithoutHtml.match(urlRegex)![0]?.includes("https://") || textWithoutHtml.match(urlRegex)![0]?.includes("http://")) {
                return textWithoutHtml.match(urlRegex)![0]
            } else {
                return `http://${textWithoutHtml.match(urlRegex)![0]}`
            }
        }
        return null
    }

    const handleRemoveLink = () => {
        dispatch(setModalData({ open: true, deleteLinkPreview: true }))
        switch (props.caller) {
            case CallerComponent.post:
                props.id && dispatch(setRoomPostId(props.id))
                break
            case CallerComponent.comment:
                props.id && dispatch(setRoomCommentId(props.id))
                break
            case CallerComponent.eventComment:
                props.id && dispatch(setRoomEventCommentId(props.id))
                break
        }
    }

    return (
        <div>
            <div
                onClick={ (e) => handleClick(e)}
                onTouchEnd={handleTouchEnd}
                className={messageBodyClass()}
                id="message-body"
                dangerouslySetInnerHTML={{ __html: linkifyHtml(normalizedHtml) }}
                >
                    </div>
            {props.date && (props.caller === CallerComponent.comment || props.caller === CallerComponent.eventComment || props.caller === CallerComponent.feed) && (
                <div className="message-body-date">{props.date} </div>
            )}

            {props.caller !== CallerComponent.eventDescription && props.showCollapsed && textWithoutHtml.length > maxContentLength && (
                <div
                    onClick={props.toggleCollapse}
                    onTouchEnd={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        props.toggleCollapse?.()
                    }}
                    className={`${
                        props.caller === CallerComponent.roomDescription
                            ? "button-textPreview-more button-textPreview-more--red"
                            : "button-textPreview-more button-textPreview-more--blue"
                    }`}
                >
                    {!props.isCollapsed ? translation.seeMoreText : translation.seeLessText}
                </div>
            )}

            {props.showPreview && props.caller !== CallerComponent.eventComment && hasLinkPreview() !== null && !hasError ? (
                <>
                    {props.showRemovePreviewIcon && (
                        <div className="clear-link-icon">
                            <div onClick={handleRemoveLink}>
                                <SmallCloseIcon />
                            </div>
                        </div>
                    )}
                    <ReactTinyLink
                        cardSize="small"
                        showGraphic={hasImage}
                        maxLine={2}
                        minLine={1}
                        proxyUrl={`${process.env.REACT_APP_BASE_API_URL}/cors-proxy/?url=`}
                        url={hasLinkPreview()}
                        onSuccess={(data: any) => {
                            if (data["image"].length > 0) {
                                setHasImage(true)
                            }
                        }}
                        onError={(error: any) => {
                            setHasError(true)
                        }}
                    />
                </>
            ) : null}
        </div>
    )
}

export default MessageBody
